/**
 * The shape of a view filter type, and the vocabulary its `matches` speaks.
 *
 * A filter exists twice: once in the backend, which decides what a view really
 * contains, and once here, so an edited row can be re-checked against the view
 * without waiting for a round trip. The two must agree -- when they don't, a row
 * appears or vanishes for a moment and then corrects itself when the server's
 * answer arrives, which reads as a glitch.
 */
import type { Field, FieldTypeName, ViewFilterTypeName } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import type { DateOperator } from './date-filters'

/**
 * What a filter says about a row.
 *
 * `null` is not "no match" -- it means the filter has no opinion, and the row is
 * kept. A filter with an empty value returns it, because a half-typed filter
 * should show everything rather than nothing. Coercing this to a boolean is the
 * single most damaging mistake available in this module: every row silently
 * disappears the moment somebody clears a filter input.
 */
export type MatchResult = boolean | null

/** Whether a filter's verdict lets a row stay in the view. */
export function isMatch(result: MatchResult): boolean {
  return result === null || result === true
}

/**
 * A field type a filter is compatible with.
 *
 * Either a type name, or a predicate over the field -- formula and lookup
 * fields are compatible based on the type they *produce*, which is a property of
 * the field rather than of its type name.
 */
export type FieldCompatibility = FieldTypeName | ((field: Field) => boolean)

/**
 * The lookups a match needs that are not on the row or the field.
 *
 * Passed in rather than read from a store, which is what keeps this layer pure
 * and testable against plain data. Extends `FieldContext` because the substring
 * filters match against rendered text, and rendering a collaborator needs the
 * workspace's current name for them. Date filters carry their timezone in the
 * filter value itself, so what this adds is the viewer's identity, which the
 * `user_is` family compares against.
 */
export interface FilterContext extends FieldContext {
  /** The signed-in user's id, for the filters that mean "me". */
  userId?: number
}

/**
 * Everything about a filter type that does not involve rendering.
 *
 * Pure by construction, like the field behaviours next door: no React, no
 * network, no registry. The runtime-mutable registry this replaces existed so
 * third-party plugins could add filters; nothing does that here, so the map is
 * static and `satisfies Record<ViewFilterTypeName, ...>` makes it exhaustive --
 * a ninety-second filter type on the backend fails to compile until it is
 * answered for here.
 */
export interface ViewFilterTypeDef<N extends ViewFilterTypeName = ViewFilterTypeName> {
  readonly type: N

  /**
   * The field types this filter can be applied to.
   *
   * A field compatible with no filter at all is disabled in the filter UI.
   */
  readonly compatibleFieldTypes: readonly FieldCompatibility[]

  /**
   * Whether the row's value satisfies the filter.
   *
   * Returns `null` when the filter has nothing to say -- see `MatchResult`.
   */
  matches: (
    rowValue: unknown,
    filterValue: string,
    field: Field,
    context?: FilterContext,
  ) => MatchResult

  /**
   * The value a newly created filter of this type starts with.
   *
   * Almost always the empty string. The timezone-sensitive date filters are the
   * exception: they bake the viewer's current timezone into the value, so the
   * filter keeps meaning the same thing when read from elsewhere.
   */
  getDefaultValue?: (field: Field) => string

  /** Normalises a value on its way to the API. */
  prepareValue?: (value: string, field: Field) => string

  /**
   * Hidden from the filter-type dropdown, but still honoured when stored.
   *
   * Filters that already exist keep working; the type just cannot be chosen
   * again. This is how the multi-step date filters superseded the older ones
   * without invalidating anybody's views.
   */
  readonly isDeprecated?: boolean

  /** Whether the filter may be used on a publicly shared view. */
  readonly isAllowedInPublicViews?: boolean

  /**
   * Whether the filter takes a value at all.
   *
   * `empty` and `is_even_and_whole` do not -- they render no input, and their
   * stored value is always the empty string.
   */
  readonly hasEditableValue?: boolean

  /**
   * Periods a multi-step date filter will not accept.
   *
   * `date_is_within` refuses `today`, because "within today" names an empty
   * span. The matcher has always known this -- it is what `dateFilter` is given
   * -- but it knew it inside a closure, where the editor could not read it and
   * would have had to keep a second copy of the same rule.
   */
  readonly refusedDateOperators?: readonly DateOperator[]
}

/**
 * Whether a filter can be applied to a field.
 *
 * `resolveFilterFieldType` maps a field to the type its filters should be
 * chosen by. For most fields that is its own type; for a formula or lookup it is
 * the type the formula produces, which is why this takes a resolver rather than
 * reading `field.type` directly.
 */
export function fieldIsCompatible(
  filter: ViewFilterTypeDef,
  field: Field,
  resolveFilterFieldType: (field: Field) => string = (f) => f.type,
): boolean {
  const compareType = resolveFilterFieldType(field)
  return filter.compatibleFieldTypes.some((entry) =>
    typeof entry === 'function' ? entry(field) : entry === compareType,
  )
}
